import {
  ACCELEROMETER_ID,
  DEVICE_MOTION_ID,
} from "../../components/DataCollection/SelectDevices/SelectAccelerometerDevices";
import { GPS_ID } from "../../components/DataCollection/SelectDevices/SelectGPSDevices";
import {
  DEVICE_ORIENTATION_ID,
  GYROSCOPE_ID,
} from "../../components/DataCollection/SelectDevices/SelectGyroscopeDevices";
import { USER_VERIFY_ID } from "../../components/DataCollection/SelectDevices/SelectUserVerifyDevices";
import { getAccelerometerRead, getDeviceMotionRead } from "./accelerometer";
import { getAllCamsData } from "./getAllCamsData";
import { getUserAgent, getClientHints } from "./getExtraData";
import getNavigatorData from "./getNavigatorData";
import { getGPSRead } from "./gps";
import { getDeviceOrientationRead, getGyroscopeRead } from "./gyroscope";
import { getAllMicrophonesData } from "./microphone";
import { getUserVerifyRead } from "./userVerify";
import getHardwareData from "./getHardwareData";
import AudioFingerprint from "./audioFingerprint";

const arrayIncludesId = (array, id) => {
  return array.some((item) => item.id === id);
};

const getSensorData = async (selectedSensors, sensorId, getDataFunction) => {
  try {
    if (arrayIncludesId(selectedSensors, sensorId)) {
      const sensorData = await getDataFunction();
      return sensorData;
    }
  } catch (e) {
    console.error(`Error getting ${sensorId} data:`, e);
    return undefined;
  }
};

/** Returns all the data we want to store. */
export const getAllData = async (video, selectedDevices, onProgress) => {
  const returnObject = { metadata: {} };

  const steps = [
    {
      name: "User Agent",
      execute: async () => {
        try {
          returnObject.userAgent = getUserAgent();
        } catch (e) {
          console.error("Error getting user agent:", e);
        }
      }
    },
    {
      name: "Client Hints",
      execute: async () => {
        try {
          returnObject.clientHints = await getClientHints();
        } catch (e) {
          console.error("Error getting client hints:", e);
        }
      }
    },
    {
      name: "Audio Fingerprint",
      execute: async () => {
        try {
          const audioFP = new AudioFingerprint();
          audioFP.run((fingerprint) => {
            returnObject.metadata.audioFingerprint = fingerprint;
          });
        } catch (e) {
          console.error("Error getting audio fingerprint:", e);
        }
      }
    },
    {
      name: "Camera Data",
      skip: selectedDevices.camera.length === 0,
      execute: async () => {
        try {
          const cameraIds = selectedDevices.camera.map(({ id }) => id);
          returnObject.metadata.camera = await getAllCamsData(video, cameraIds);
        } catch (e) {
          console.error("Error getting cameras information:", e);
        }
      }
    },
    {
      name: "Accelerometer Data",
      skip: selectedDevices.accelerometer.length === 0,
      execute: async () => {
        const accelerometer = await getSensorData(
          selectedDevices.accelerometer,
          ACCELEROMETER_ID,
          getAccelerometerRead
        );
        const deviceMotion = await getSensorData(
          selectedDevices.accelerometer,
          DEVICE_MOTION_ID,
          getDeviceMotionRead
        );
        // accelerometer/deviceMotion each carry their own undefined/[]/data
        // value (see accelerometer.js); omit this section only if both are undefined.
        returnObject.metadata.accelerometer =
          accelerometer || deviceMotion ? { accelerometer, deviceMotion } : undefined;
      }
    },
    {
      name: "Gyroscope Data",
      skip: selectedDevices.gyroscope.length === 0,
      execute: async () => {
        const gyroscope = await getSensorData(
          selectedDevices.gyroscope,
          GYROSCOPE_ID,
          getGyroscopeRead
        );
        const deviceOrientation = await getSensorData(
          selectedDevices.gyroscope,
          DEVICE_ORIENTATION_ID,
          getDeviceOrientationRead
        );
        // gyroscope/deviceOrientation each carry their own undefined/[]/data
        // value (see gyroscope.js); omit this section only if both are undefined.
        returnObject.metadata.gyroscope =
          gyroscope || deviceOrientation ? { gyroscope, deviceOrientation } : undefined;
      }
    },
    {
      name: "GPS Data",
      skip: selectedDevices.gps.length === 0,
      execute: async () => {
        const gps = await getSensorData(selectedDevices.gps, GPS_ID, getGPSRead);
        returnObject.metadata.gps = gps;
      }
    },
    {
      name: "User Verify Data",
      skip: selectedDevices.userVerify.length === 0,
      execute: async () => {
        const userVerify = await getSensorData(
          selectedDevices.userVerify,
          USER_VERIFY_ID,
          getUserVerifyRead
        );
        returnObject.metadata.userVerify = { userVerify };
      }
    },
    {
      name: "Microphone Data",
      skip: selectedDevices.microphone.length === 0,
      execute: async () => {
        try {
          const microphoneIds = selectedDevices.microphone.map(({ id }) => id);
          returnObject.metadata.microphone = await getAllMicrophonesData(microphoneIds);
        } catch (e) {
          console.error("Error getting microphone data:", e);
        }
      }
    },
    {
      name: "Navigator Data",
      skip: selectedDevices.navigator.length === 0,
      execute: async () => {
        returnObject.metadata.navigator = await getNavigatorData(selectedDevices.navigator);
      }
    },
    {
      name: "Hardware Data",
      execute: async () => {
        returnObject.metadata.hardware = await getHardwareData();
      }
    }
  ];

  const activeSteps = steps.filter(step => !step.skip);
  const totalSteps = activeSteps.length;

  for (let i = 0; i < activeSteps.length; i++) {
    const step = activeSteps[i];
    if (onProgress) {
      // for display purposes, start from step 1, not 0
      onProgress(i + 1, totalSteps, step.name);
    }
    await step.execute();
  }

  return returnObject;
};
